'use client';

import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { Badge, Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import type { AffiliatePlatform } from '@/data/affiliates';

export function AffiliateCard({ platform }: { platform: AffiliatePlatform }) {
  return (
    <Card className="flex h-full flex-col gap-5" interactive>
      {/* Badge on its own row so cards line up regardless of platform name length. */}
      <div className="flex flex-col gap-3">
        <Badge tone={platform.accent} className="self-start">
          Licensed partner
        </Badge>
        <div>
          <h3 className="text-2xl text-white">{platform.name}</h3>
          <p className="text-content-subtle mt-1 text-xs font-semibold tracking-[0.18em] uppercase">
            {platform.category}
          </p>
        </div>
      </div>

      {platform.image ? (
        <div className="border-line relative h-44 w-full overflow-hidden rounded-2xl border">
          <OptimizedImage
            src={platform.image}
            alt={platform.imageAlt ?? `${platform.name} promotional artwork`}
            fill
            sizes="(max-width: 1024px) 100vw, 33vw"
            className="object-cover object-top"
          />
        </div>
      ) : null}

      <p className="text-content-muted text-sm leading-relaxed">{platform.description}</p>

      <ul className="flex flex-col gap-2.5">
        {platform.offers.map((offer) => (
          <li key={offer} className="text-content-muted flex items-start gap-2.5 text-sm">
            <Icon name="check" className="text-brand-bright mt-0.5 h-4 w-4" />
            <span>{offer}</span>
          </li>
        ))}
      </ul>

      <div className="border-line-strong/60 bg-ink/50 mt-auto flex flex-col gap-3 rounded-2xl border p-4">
        {platform.code ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-content-subtle text-xs tracking-wider uppercase">Affiliate code</span>
            <code className="text-gold font-display text-lg font-bold tracking-widest">{platform.code}</code>
          </div>
        ) : (
          <p className="text-content-muted text-sm">{platform.codeNote}</p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button size="sm" variant="secondary" fullWidth to={`/affiliates#request-${platform.id}`}>
            Request code
          </Button>
          {platform.website ? (
            <Button size="sm" variant="ghost" fullWidth href={platform.website}>
              Visit platform
              <Icon name="link" className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
